/**
 * Runtime configuration accessor.
 *
 * External endpoints / keys / IDs are supplied to the already-built static app by the
 * container at startup (via `env-config.json`, see `docker-entrypoint.sh`), NOT baked in at
 * webpack build time. All application code reads these values through `getConfig()` at call
 * time — never at module-evaluation time — so a single image can be configured per environment
 * with no rebuild.
 *
 * When no runtime config has been loaded (local dev server, unit tests, or before
 * `loadRuntimeConfig()` resolves) the accessor falls back to `DEV_DEFAULTS`, which reproduce
 * today's hardcoded values so existing behavior is unchanged.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/** Strip a single trailing slash so `${base}/path` never doubles up. */
var stripTrailingSlash = function (url) {
    return typeof url === 'string' ? url.replace(/\/+$/, '') : url;
};
var toBoolean = function (value) {
    return value === true || value === 'true';
};
/**
 * Development / test defaults. These equal today's hardcoded values so `npm run ...:start` and
 * Jest run with no container.
 */
export var DEV_DEFAULTS = {
    materialStorageUrl: 'https://qas.api.phoenixcontact.com',
    materialStorageSubscriptionKey: '551adbb56abe4f0d86334e1ab3bd3918',
    userAuthUrl: 'https://www8.phoenixcontact.com',
    userAuthTokenUrl: '',
    pxcPortalUrl: 'https://www8.phoenixcontact.com',
    customerSupportUrl: 'https://www.phoenixcontact.com/de-de/service-und-support/kontaktieren-sie-uns',
    projectDataItemId: '1538213',
    debug: false,
    // Local dev / standalone served the editor at full height (the old `localhost` heuristic),
    // so the default-without-a-container keeps that behavior.
    unconstrainedHeight: true,
};
var _config = null;
/** Normalize a raw config file into a fully-populated, slash-normalized RuntimeConfig. */
var normalize = function (raw) { return ({
    materialStorageUrl: stripTrailingSlash(raw.materialStorageUrl || DEV_DEFAULTS.materialStorageUrl),
    materialStorageSubscriptionKey: raw.materialStorageSubscriptionKey ||
        DEV_DEFAULTS.materialStorageSubscriptionKey,
    userAuthUrl: stripTrailingSlash(raw.userAuthUrl || DEV_DEFAULTS.userAuthUrl),
    userAuthTokenUrl: stripTrailingSlash(raw.userAuthTokenUrl || DEV_DEFAULTS.userAuthTokenUrl),
    pxcPortalUrl: stripTrailingSlash(raw.pxcPortalUrl || DEV_DEFAULTS.pxcPortalUrl),
    customerSupportUrl: raw.customerSupportUrl || DEV_DEFAULTS.customerSupportUrl,
    projectDataItemId: raw.projectDataItemId || DEV_DEFAULTS.projectDataItemId,
    debug: toBoolean(raw.debug),
    unconstrainedHeight: toBoolean(raw.unconstrainedHeight),
}); };
/**
 * Fetch `env-config.json` from the bundle's own serving origin and cache it.
 *
 * Fetching from the bundle origin (rather than reading a `window.__CONFIG__` global) is
 * host-independent: the editor works even when embedded cross-origin in a third-party host page
 * that never loaded a config script. Must be awaited before `ReactDOM.render`. On any failure the
 * accessor stays on `DEV_DEFAULTS`.
 *
 * @param origin Base URL (with trailing slash) the bundle was served from.
 */
export var loadRuntimeConfig = function (origin) { return __awaiter(void 0, void 0, void 0, function () {
    var base, raw, error_1;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                _a.trys.push([0, 2, , 3]);
                base = origin.endsWith('/') ? origin : "".concat(origin, "/");
                return [4 /*yield*/, fetch("".concat(base, "env-config.json")).then(function (res) {
                        if (!res.ok) {
                            throw new Error("env-config.json HTTP ".concat(res.status));
                        }
                        return res.json();
                    })];
            case 1:
                raw = _a.sent();
                _config = normalize(raw);
                return [3 /*break*/, 3];
            case 2:
                error_1 = _a.sent();
                // Fall back to dev defaults (no container / standalone dev). Keep loud so misconfigured
                // deployments are noticeable in the console.
                console.warn('[loadRuntimeConfig] using DEV_DEFAULTS:', error_1);
                _config = null;
                return [3 /*break*/, 3];
            case 3: return [2 /*return*/];
        }
    });
}); };
/** Read the active runtime config. Returns loaded config, or `DEV_DEFAULTS` if none. */
export var getConfig = function () { return _config !== null && _config !== void 0 ? _config : DEV_DEFAULTS; };
